import type { DataValue, PictureValueType } from '../../data/sources';
import { useSocialFeedSettings } from '../../utils';
import { useFormattedLinksText } from './useFormattedLinksText';

export type PostDataValue = Omit<
  DataValue<string | PictureValueType[]>,
  'source' | 'sourceLink' | 'repost'
> & {
  hasMedia: boolean;
  hasTextLink: boolean;
  textLink: string | undefined;
  followButtonLink: string;
  showMedia: boolean;
  showTextLink: boolean;
};

export const usePostData = (
  data?: DataValue<string | PictureValueType[]>
): PostDataValue | undefined => {
  const { enableExternalLinks: settingsEnableExternalLinks, postAction } =
    useSocialFeedSettings();

  const {
    id = '',
    text,
    media,
    user,
    sourceLink,
    timestamp = null,
    repost
  } = data ?? {};
  const {
    type,
    description: linkDescription,
    title: linkTitle,
    src: mediaSrc,
    items: mediaItems
  } = media ?? {};

  const textToFormat = text || linkDescription || linkTitle || '';
  const formattedTextData = useFormattedLinksText(textToFormat);
  const { formattedText, textLink } = formattedTextData ?? {};

  if (!data) {
    return;
  }

  // Не объединяем с верхней деструктуризацией, чтобы TS не определял тип | undefined
  const { source } = data;

  const isLinkedinLinkMedia = source.type === 'linkedin' && type === 'link';

  const hasTextLink =
    isLinkedinLinkMedia || (!!textLink && !formattedText?.includes('hashtag'));

  const isNotEmptyMedia = !!(mediaSrc?.length || mediaItems?.length);
  const hasMedia = isNotEmptyMedia && !isLinkedinLinkMedia && type !== 'audio';

  const enableExternalLinks =
    settingsEnableExternalLinks || postAction === 'follow';

  return {
    id,
    user,
    media,
    hasMedia,
    timestamp,
    textLink,
    showMedia: hasMedia && !repost,
    showTextLink: hasTextLink && !hasMedia && !repost,
    hasTextLink: enableExternalLinks && hasTextLink,
    text: formattedText,
    followButtonLink: sourceLink ?? user?.url ?? ''
  };
};
